import { geocodeByPlaceId, getLatLng } from "react-google-places-autocomplete";

export const AUSTRALIA_AUTOCOMPLETION_REQUEST = {
  componentRestrictions: { country: ["au"] },
};

export const AUSTRALIAN_ADDRESS_ERROR =
  "Please select a valid Australian address from the suggestions.";

export type AustralianAddressDetails = {
  addressName: string;
  addressLine: string;
  postalCode: string;
  country: string;
  region: string;
  city: string;
  latitude: number;
  longitude: number;
};

function findComponent(components: any[], type: string): any {
  return components.find((component) => component?.types?.includes(type));
}

export function parseAustralianGeocoderResult(
  result: any,
  coordinates: { lat: number; lng: number }
): AustralianAddressDetails {
  if (!result?.formatted_address) throw new Error(AUSTRALIAN_ADDRESS_ERROR);

  const components = result.address_components ?? [];
  const countryComponent = findComponent(components, "country");
  const countryCode = String(countryComponent?.short_name ?? "").toUpperCase();
  if (countryCode !== "AU") throw new Error(AUSTRALIAN_ADDRESS_ERROR);

  const streetNumber = findComponent(components, "street_number")?.long_name ?? "";
  const route = findComponent(components, "route")?.long_name ?? "";

  return {
    addressName: result.formatted_address,
    addressLine: [streetNumber, route].filter(Boolean).join(" "),
    postalCode: findComponent(components, "postal_code")?.long_name ?? "",
    country: countryComponent?.long_name ?? "Australia",
    region:
      findComponent(components, "administrative_area_level_1")?.long_name ?? "",
    city:
      findComponent(components, "locality")?.long_name ??
      findComponent(components, "postal_town")?.long_name ??
      findComponent(components, "sublocality")?.long_name ??
      "",
    latitude: coordinates.lat,
    longitude: coordinates.lng,
  };
}

export async function resolveAustralianPlace(
  placeId: string
): Promise<AustralianAddressDetails> {
  if (!placeId) throw new Error(AUSTRALIAN_ADDRESS_ERROR);
  const results = await geocodeByPlaceId(placeId);
  const result = results?.[0];
  if (!result) throw new Error(AUSTRALIAN_ADDRESS_ERROR);
  const coordinates = await getLatLng(result);
  return parseAustralianGeocoderResult(result, coordinates);
}

export function serializeAustralianAddress(
  details: AustralianAddressDetails
): string {
  const { latitude, longitude, ...storedAddress } = details;
  return JSON.stringify(storedAddress);
}
